import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { v4 as uuidv4 } from 'uuid';
import { fieldTypes, generateGroupId, groupDefinitions } from '../utils/fieldTypes';
import { allIcons, popularIcons } from '../utils/icons';
import { presetFields } from '../utils/presetFields';
import './Modal.css';

const Modal = ({ type, data, onSave, onClose, existingFields = [], allowMultiple = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    icon: 'home',
    type: 'text',
    label: '',
    placeholder: '',
    required: false,
    adminOnly: false,
    enabled: true
  });

  const [iconSearch, setIconSearch] = useState('');
  const [showAllIcons, setShowAllIcons] = useState(false);
  const [fieldMode, setFieldMode] = useState('custom');
  const [selectedPresetFields, setSelectedPresetFields] = useState([]);
  const [presetSearch, setPresetSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('basic');
  
  // NEW: Check if we're editing an existing group field
  const [isEditingGroup, setIsEditingGroup] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (data) {
      if (type === 'tab' || type === 'section' || type === 'tabsection') {
        setFormData({
          title: data.title || '',
          icon: data.icon || 'home'
        });
      } else {
        // Check if this is a group field being edited
        const isGroup = data.group_type || 
                       (data.type && fieldTypes.find(ft => ft.value === data.type)?.isGroup);
        
        setIsEditingGroup(isGroup);
        
        setFormData({
          type: data.type || 'text',
          label: data.label || '',
          placeholder: data.placeholder || '',
          required: data.required || false,
          adminOnly: data.adminOnly || false,
          enabled: data.enabled !== false
        });
        
        if (data.id) {
          const isPreset = Object.values(presetFields).some(category => 
            category.fields.some(field => data.id.startsWith(field.id))
          );
          
          if (isPreset) {
            setFieldMode('preset');
            Object.values(presetFields).forEach(category => {
              category.fields.forEach(field => {
                if (data.id.startsWith(field.id)) {
                  setActiveCategory(Object.keys(presetFields).find(key => presetFields[key].name === category.name));
                  setSelectedPresetFields([field.id]);
                }
              });
            });
          }
        }
      }
    } else {
      setFormData({
        title: '',
        icon: 'home',
        type: 'text',
        label: '',
        placeholder: '',
        required: false,
        adminOnly: false,
        enabled: true
      });
      setFieldMode('custom');
      setSelectedPresetFields([]);
      setActiveCategory('basic');
      setIsEditingGroup(false);
    }
  }, [data, type]);

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: inputType === 'checkbox' ? checked : value 
    }));
  };

  const handleIconSelect = (icon) => {
    setFormData(prev => ({ ...prev, icon }));
  };

  /**
   * Handle creating group fields with proper unique field names
   */
  const createGroupFields = (groupType) => {
    const groupDefinition = groupDefinitions[groupType];
    if (!groupDefinition) return [];
    
    // Generate a SINGLE group ID for ALL fields in this group
    const groupId = generateGroupId(groupType);
    const timestamp = Date.now();
    const uniqueSuffix = uuidv4().substring(0, 8);
    
    // Create a UNIQUE base ID for this group
    const groupBaseId = `${groupType}_${timestamp}_${uniqueSuffix}`;
    
    return groupDefinition.fields.map((fieldDef, index) => {
      // Determine the metaKey - use fieldDef.metaKey or fallback to fieldDef.id
      const metaKey = fieldDef.metaKey || fieldDef.id;
      
      // Generate a consistent field ID
      const fieldId = `${groupBaseId}_${metaKey}`;
      
      // This ensures each group has unique meta keys
      const fieldName = `${groupBaseId}_${metaKey}`;
      
      // For backward compatibility, store the legacy key pattern
      const legacyName = `_hvnly_${groupType}_${metaKey}`;
      
      const fieldData = {
        id: fieldId,
        name: fieldName,
        type: fieldDef.type,
        label: fieldDef.label,
        placeholder: fieldDef.placeholder || '',
        required: fieldDef.required || false,
        adminOnly: formData.adminOnly,
        enabled: formData.enabled !== false,
        locked: false,
        order: existingFields.length + index,
        group_id: groupId,
        group_type: groupType,
        group_name: groupDefinition.name,
        group_position: index,
        group_total: groupDefinition.fieldCount,
        group_collapsed: false,
        group_base_id: groupBaseId,
        metaKey: metaKey,
        legacy_name: legacyName,
        description: fieldDef.description || '',
        
        // Store additional metadata
        group_timestamp: timestamp,
        group_unique_id: uniqueSuffix
      };

      // ONLY add show_in_sidebar for property_docs group
      if (groupType === 'property_docs') {
        fieldData.show_in_sidebar = true;
      }

      // For map fields, store relationship field names
      if (groupType === 'map') {
        fieldData.address_field_name = `${groupBaseId}_address`;
        fieldData.lat_field_name = `${groupBaseId}_latitude`;
        fieldData.lng_field_name = `${groupBaseId}_longitude`;
        fieldData.map_field_name = `${groupBaseId}_preview`;
      }

      return fieldData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (type === 'field' && fieldMode === 'preset') {
      // Handle preset fields
      const fieldsToSave = selectedPresetFields.map(fieldId => {
        let presetField = null;
        Object.values(presetFields).forEach(category => {
          const found = category.fields.find(field => field.id === fieldId);
          if (found) presetField = found;
        });
        
        if (!presetField) return null;
        
        return {
          ...presetField,
          id: presetField.id,
          label: presetField.defaultLabel,
          placeholder: presetField.defaultPlaceholder,
          required: presetField.defaultRequired,
          adminOnly: presetField.defaultAdminOnly,
          enabled: true,
          locked: false,
          options: presetField.options || {}
        };
      }).filter(field => field !== null);
      
      if (fieldsToSave.length > 0) {
        onSave(fieldsToSave);
      }
    } else {
      // Handle custom fields or group fields
      const selectedFieldType = formData.type;
      const fieldTypeDef = fieldTypes.find(ft => ft.value === selectedFieldType);
      
      // Check if this is a group field type
      if (fieldTypeDef?.isGroup) {
        // Create all fields for the group
        const groupFields = createGroupFields(fieldTypeDef.groupType);
        onSave(groupFields);
      } else {
        // Handle normal field
        onSave(formData);
      }
    }
  };

  const handlePresetFieldToggle = (fieldId) => {
    setSelectedPresetFields(prev => {
      if (data && !Array.isArray(data) || !allowMultiple) {
        return prev.includes(fieldId) ? [] : [fieldId];
      }
      
      if (prev.includes(fieldId)) {
        return prev.filter(id => id !== fieldId);
      } else {
        return [...prev, fieldId];
      }
    });
  };

  const isPresetFieldInUse = (fieldId) => {
    if (data && data.id && data.id.startsWith(fieldId)) {
      return false;
    }
    
    return existingFields.some(field => field.id && field.id.startsWith(fieldId));
  };

  const getFilteredPresetFields = () => {
    const category = presetFields[activeCategory];
    if (!category) return [];
    
    if (!presetSearch.trim()) return category.fields;
    
    return category.fields.filter(field => 
      field.defaultLabel.toLowerCase().includes(presetSearch.toLowerCase()) ||
      (field.defaultPlaceholder && field.defaultPlaceholder.toLowerCase().includes(presetSearch.toLowerCase()))
    );
  };

  const getFilteredIcons = () => {
    if (iconSearch.trim()) {
      return allIcons.filter(icon => 
        icon.toLowerCase().includes(iconSearch.toLowerCase())
      );
    }
    
    return showAllIcons ? allIcons : popularIcons;
  };

  const displayedIcons = getFilteredIcons();
  const filteredPresetFields = getFilteredPresetFields();

  // NEW: Filter field types based on editing mode
  const getAvailableFieldTypes = () => {
    if (isEditingGroup) {
      // When editing a group, only show group field types
      return fieldTypes.filter(field => field.isGroup === true);
    }
    // When adding new field or editing normal field, show all types
    return fieldTypes;
  };

  const availableFieldTypes = getAvailableFieldTypes();

  return (
    <div className="hvnly--builder--int_api__property_builder_modal_overlay">
      <div className="hvnly--builder--int_api__property_builder_modal hvnly--builder--int_api__property_builder_wide_modal">
        <div className="hvnly--builder--int_api__property_builder_modal_header">
          <h2>
            {data ? __('Edit', 'havenlytics') : __('Add New', 'havenlytics')} 
            {type === 'tab' || type === 'section' || type === 'tabsection' 
              ? __(' Section', 'havenlytics') 
              : (isEditingGroup ? __(' Group Field', 'havenlytics') : __(' Field', 'havenlytics'))}
          </h2>
          <button onClick={onClose} className="hvnly--builder--int_api__modal_close_btn">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="hvnly--builder--int_api__property_builder_modal_body">
            {type === 'tab' || type === 'section' || type === 'tabsection' ? (
              <>
                <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                  <label className="hvnly--builder--int_api__property_builder_modal_label">{__('Section Title', 'havenlytics')}</label>
                  <input
                    type="text"
                    className="hvnly--builder--int_api__property_builder_modal_input"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleChange}
                    placeholder={__('Enter section title', 'havenlytics')}
                    required
                  />
                </div>
                
                <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                  <label className="hvnly--builder--int_api__property_builder_modal_label">{__('Select Icon', 'havenlytics')}</label>
                  
                  <div className="hvnly--builder--int_api__icon_search">
                    <input
                      type="text"
                      className="hvnly--builder--int_api__property_builder_modal_input"
                      placeholder={__('Search icons...', 'havenlytics')}
                      value={iconSearch}
                      onChange={(e) => setIconSearch(e.target.value)}
                    />
                    <button
                      type="button"
                      className="hvnly--builder--int_api__show_all_icons_btn"
                      onClick={() => setShowAllIcons(!showAllIcons)}
                    >
                      {showAllIcons ? __('Show Popular Only', 'havenlytics') : __('Show All Icons', 'havenlytics')}
                    </button>
                  </div>
                  
                  <div className="hvnly--builder--int_api__property_builder_icon_picker">
                    {displayedIcons.map(icon => (
                      <div
                        key={icon}
                        className={`hvnly--builder--int_api__property_builder_icon_option ${formData.icon === icon ? 'selected' : ''}`}
                        onClick={() => handleIconSelect(icon)}
                        title={icon}
                      >
                        <i className={`fas fa-${icon}`}></i>
                        <div className="icon-name">{icon}</div>
                        {formData.icon === icon && (
                          <div className="icon-checkmark">
                            <i className="fas fa-check"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {displayedIcons.length === 0 && (
                    <p className="hvnly--builder--int_api__no_icons_found">
                      {__('No icons found matching', 'havenlytics')} "{iconSearch}"
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Show preset toggle only for non-group fields or when adding new */}
                {!isEditingGroup && (
                  <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                    <div className="hvnly--builder--int_api__field_mode_toggle">
                      <button
                        type="button"
                        className={`hvnly--builder--int_api__field_mode_btn ${fieldMode === 'preset' ? 'active' : ''}`}
                        onClick={() => setFieldMode('preset')}
                      >
                        <i className="fas fa-star"></i> {__('Preset Fields', 'havenlytics')}
                      </button>
                      <button
                        type="button"
                        className={`hvnly--builder--int_api__field_mode_btn ${fieldMode === 'custom' ? 'active' : ''}`}
                        onClick={() => setFieldMode('custom')}
                      >
                        <i className="fas fa-edit"></i> {__('Custom Field', 'havenlytics')}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Show editing group notice */}
                {isEditingGroup && (
                  <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                    <div className="hvnly--builder--int_api__editing_group_notice">
                      <i className="fas fa-info-circle"></i>
                      <span>
                        {__('You are editing a group field. You can only replace it with another group field type.', 'havenlytics')}
                      </span>
                    </div>
                  </div>
                )}
                
                {fieldMode === 'preset' && !isEditingGroup ? (
                  <div className="hvnly--builder--int_api__property_builder_modal_form_group hvnly--builder--int_api__preset_fields_container">
                    {allowMultiple && (
                      <div className="hvnly--builder--int_api__multiple_selection_info">
                        <i className="fas fa-info-circle"></i>
                        <span>{__('You can select multiple fields by clicking on them', 'havenlytics')}</span>
                      </div>
                    )}
                    
                    <div className="hvnly--builder--int_api__preset_categories">
                      {Object.entries(presetFields).map(([key, category]) => (
                        <button
                          key={key}
                          type="button"
                          className={`hvnly--builder--int_api__preset_category_tab ${activeCategory === key ? 'active' : ''}`}
                          onClick={() => setActiveCategory(key)}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                    
                    <div className="hvnly--builder--int_api__preset_fields_header">
                      <div className="hvnly--builder--int_api__preset_search">
                        <i className="fas fa-search"></i>
                        <input
                          type="text"
                          placeholder={__('Search preset fields...', 'havenlytics')}
                          value={presetSearch}
                          onChange={(e) => setPresetSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="hvnly--builder--int_api__preset_fields_list">
                      {filteredPresetFields.length > 0 ? (
                        filteredPresetFields.map(field => {
                          const isInUse = isPresetFieldInUse(field.id);
                          const isSelected = selectedPresetFields.includes(field.id);
                          
                          return (
                            <div
                              key={field.id}
                              className={`hvnly--builder--int_api__preset_field_item ${isInUse ? 'in-use' : ''} ${isSelected ? 'selected' : ''}`}
                              onClick={() => !isInUse && handlePresetFieldToggle(field.id)}
                            >
                              <div className="hvnly--builder--int_api__preset_field_checkbox">
                                {isSelected && <i className="fas fa-check"></i>}
                              </div>
                              <div className="hvnly--builder--int_api__preset_field_info">
                                <div className="hvnly--builder--int_api__preset_field_label">
                                  <i className={`fas fa-${field.icon}`}></i>
                                  {field.defaultLabel}
                                  {field.defaultRequired && <span className="hvnly--builder--int_api__field_required_badge">{__('Required', 'havenlytics')}</span>}
                                </div>
                                <div className="hvnly--builder--int_api__preset_field_description">
                                  {field.defaultPlaceholder}
                                </div>
                                <div className="hvnly--builder--int_api__preset_field_type">
                                  {__('Type:', 'havenlytics')} {fieldTypes.find(ft => ft.value === field.type)?.label || field.type}
                                </div>
                              </div>
                              {isInUse && (
                                <div className="hvnly--builder--int_api__preset_field_status">
                                  <span className="hvnly--builder--int_api__preset_field_in_use">{__('Already in use', 'havenlytics')}</span>
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="hvnly--builder--int_api__no_preset_fields_found">
                          <i className="fas fa-search"></i>
                          <p>{__('No preset fields found matching', 'havenlytics')} "{presetSearch}"</p>
                        </div>
                      )}
                    </div>
                    
                    {selectedPresetFields.length > 0 && (
                      <div className="hvnly--builder--int_api__selected_preset_fields">
                        <h4>
                          <i className="fas fa-check-circle"></i> 
                          {__('Selected Fields', 'havenlytics')} ({selectedPresetFields.length})
                        </h4>
                        <div className="hvnly--builder--int_api__selected_preset_fields_list">
                          {selectedPresetFields.map(fieldId => {
                            let presetField = null;
                            Object.values(presetFields).forEach(category => {
                              const found = category.fields.find(field => field.id === fieldId);
                              if (found) presetField = found;
                            });
                            
                            return presetField ? (
                              <div key={fieldId} className="hvnly--builder--int_api__selected_preset_field_item">
                                <i className={`fas fa-${presetField.icon}`}></i>
                                <span>{presetField.defaultLabel}</span>
                                <button
                                  type="button"
                                  className="hvnly--builder--int_api__remove_preset_field_btn"
                                  onClick={() => handlePresetFieldToggle(fieldId)}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                      <label className="hvnly--builder--int_api__property_builder_modal_label">
                        {isEditingGroup ? __('Group Field Type', 'havenlytics') : __('Field Type', 'havenlytics')}
                      </label>
                      <select
                        className="hvnly--builder--int_api__property_builder_modal_select"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        required
                      >
                        {availableFieldTypes.map(field => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                            {field.isGroup ? ` (${groupDefinitions[field.groupType]?.fieldCount || 0} fields)` : ''}
                          </option>
                        ))}
                      </select>
                      {isEditingGroup && availableFieldTypes.length === 0 && (
                        <p className="hvnly--builder--int_api__field_type_warning">
                          {__('No group field types available', 'havenlytics')}
                        </p>
                      )}
                    </div>
                    
                    {!fieldTypes.find(ft => ft.value === formData.type)?.isGroup && (
                      <>
                        <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                          <label className="hvnly--builder--int_api__property_builder_modal_label">{__('Field Label', 'havenlytics')}</label>
                          <input
                            type="text"
                            className="hvnly--builder--int_api__property_builder_modal_input"
                            name="label"
                            value={formData.label || ''}
                            onChange={handleChange}
                            placeholder={__('Enter field label', 'havenlytics')}
                            required
                          />
                        </div>
                        
                        <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                          <label className="hvnly--builder--int_api__property_builder_modal_label">{__('Placeholder Text', 'havenlytics')}</label>
                          <input
                            type="text"
                            className="hvnly--builder--int_api__property_builder_modal_input"
                            name="placeholder"
                            value={formData.placeholder || ''}
                            onChange={handleChange}
                            placeholder={__('Enter placeholder text', 'havenlytics')}
                          />
                        </div>
                      </>
                    )}
                    
                    {fieldTypes.find(ft => ft.value === formData.type)?.isGroup && (
                      <div className="hvnly--builder--int_api__property_builder_modal_form_group">
                        <div className="hvnly--builder--int_api__group_info_banner">
                          <i className="fas fa-info-circle"></i>
                          <p>
                            {__('This will create a group of', 'havenlytics')} 
                            <strong> {groupDefinitions[fieldTypes.find(ft => ft.value === formData.type)?.groupType]?.fieldCount} </strong>
                            {__('related fields that will be visually grouped together.', 'havenlytics')}
                          </p>
                        </div>
                        
                        {/* Enhanced info for property documents group */}
                        {fieldTypes.find(ft => ft.value === formData.type)?.groupType === 'property_docs' && (
                          <div className="hvnly--builder--int_api__group_info_banner hvnly--builder--int_api__documents_info" style={{ display: 'none' }}>
                            <i className="fas fa-file-pdf"></i>
                            <div>
                              <p>
                                <strong>{__('Property Documents:', 'havenlytics')}</strong> {__('Creates a document manager with icon, label, and URL fields.', 'havenlytics')}
                              </p>
                              <ul className="hvnly--builder--int_api__documents_features_list">
                                <li><i className="fas fa-check-circle"></i> {__('Use the toggle button in group header to show/hide in sidebar', 'havenlytics')}</li>
                                <li><i className="fas fa-check-circle"></i> {__('Documents will appear in the property sidebar widget when enabled', 'havenlytics')}</li>
                                <li><i className="fas fa-check-circle"></i> {__('FontAwesome icons: file-pdf, print, video, file-word, etc.', 'havenlytics')}</li>
                              </ul>
                            </div>
                          </div>
                        )}
                        
                        {/* Show warning when replacing a group */}
                        {isEditingGroup && data?.group_type && data.group_type !== formData.type && (
                          <div className="hvnly--builder--int_api__group_replace_warning">
                            <i className="fas fa-exclamation-triangle"></i>
                            <p>
                              <strong>{__('Warning:', 'havenlytics')}</strong> {__('Replacing this group will remove all existing fields in the current group and create a new group with different field structure.', 'havenlytics')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="hvnly--builder--int_api__property_builder_modal_form_group hvnly--builder--int_api__modal_checkbox_group">
                      <label className="hvnly--builder--int_api__property_builder_modal_checkbox">
                        <input
                          type="checkbox"
                          name="required"
                          checked={formData.required}
                          onChange={handleChange}
                        />
                        <span className="hvnly--builder--int_api__checkbox_custom"></span>
                        {__('Required Field', 'havenlytics')}
                      </label>
                      
                      <label className="hvnly--builder--int_api__property_builder_modal_checkbox">
                        <input
                          type="checkbox"
                          name="adminOnly"
                          checked={formData.adminOnly}
                          onChange={handleChange}
                        />
                        <span className="hvnly--builder--int_api__checkbox_custom"></span>
                        {__('Administrative Only', 'havenlytics')}
                      </label>

                      <label className="hvnly--builder--int_api__property_builder_modal_checkbox">
                        <input
                          type="checkbox"
                          name="enabled"
                          checked={formData.enabled}
                          onChange={handleChange}
                        />
                        <span className="hvnly--builder--int_api__checkbox_custom"></span>
                        {__('Field Enabled', 'havenlytics')}
                      </label>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          
          <div className="hvnly--builder--int_api__property_builder_modal_footer">
            <button
              type="button"
              className="hvnly--builder--int_api__property_builder_modal_btn hvnly--builder--int_api__property_builder_modal_btn_secondary"
              onClick={onClose}
            >
              {__('Cancel', 'havenlytics')}
            </button>
            <button
              type="submit"
              className="hvnly--builder--int_api__property_builder_modal_btn hvnly--builder--int_api__property_builder_modal_btn_primary"
              disabled={
                (type === 'field' && fieldMode === 'preset' && selectedPresetFields.length === 0 && !isEditingGroup) ||
                (type !== 'field' && !formData.title.trim())
              }
            >
              {type === 'field' && fieldMode === 'preset' && !isEditingGroup
                ? `${__('Add', 'havenlytics')} ${selectedPresetFields.length} ${selectedPresetFields.length !== 1 ? __('Fields', 'havenlytics') : __('Field', 'havenlytics')}` 
                : (data ? __('Update', 'havenlytics') : __('Add', 'havenlytics')) + ' ' + (type === 'tab' || type === 'section' || type === 'tabsection' ? __('Section', 'havenlytics') : (isEditingGroup ? __('Group', 'havenlytics') : __('Field', 'havenlytics')))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Modal;